import { useMemo } from 'react';
import { STATUS_ORDER, STATUS_LABELS, Priority, DISPOSITION_ORDER, DISPOSITION_LABELS } from '@launchpad/shared';
import { useFilterStore, NO_DISPOSITION } from '../../store/useFilterStore.js';
import { useAppStore } from '../../store/useAppStore.js';

export function FilterPanel({ boardType = 'application' }) {
  const applications = useAppStore((s) => s.applications);
  const { statusFilter, tagFilter, priorityFilter, dispositionFilter, dateRange, setStatusFilter, setTagFilter, setPriorityFilter, setDispositionFilter, setDateRange, clearAll } = useFilterStore();
  const isApplicationBoard = boardType !== 'networking';

  const allTags = useMemo(() => {
    const tags = new Set();
    for (const app of applications) {
      for (const t of app.tags || []) tags.add(t);
    }
    return [...tags].sort();
  }, [applications]);

  const hasFilters = statusFilter.length || tagFilter.length || priorityFilter.length || dispositionFilter.length || dateRange.from || dateRange.to;

  function toggleItem(arr, item, setter) {
    setter(arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item]);
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      {/* Status — hidden on mobile since MobileBoard has tabs */}
      <div className="hidden sm:flex gap-1">
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            onClick={() => toggleItem(statusFilter, s, setStatusFilter)}
            className={`rounded px-2 py-0.5 ${statusFilter.includes(s) ? 'bg-primary-100 text-primary-700 font-medium' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {/* Priority */}
      <div className="flex gap-1">
        {Object.values(Priority).map((p) => (
          <button
            key={p}
            onClick={() => toggleItem(priorityFilter, p, setPriorityFilter)}
            className={`rounded px-2 py-0.5 capitalize ${priorityFilter.includes(p) ? 'bg-primary-100 text-primary-700 font-medium' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Tags */}
      {allTags.length > 0 && (
        <select
          value=""
          onChange={(e) => e.target.value && toggleItem(tagFilter, e.target.value, setTagFilter)}
          className="rounded border border-slate-300 px-2 py-0.5 text-xs"
        >
          <option value="">Tags…</option>
          {allTags.map((t) => (
            <option key={t} value={t}>{tagFilter.includes(t) ? `✓ ${t}` : t}</option>
          ))}
        </select>
      )}

      {/* Disposition — application board only */}
      {isApplicationBoard && (
        <select
          value=""
          onChange={(e) => e.target.value && toggleItem(dispositionFilter, e.target.value, setDispositionFilter)}
          className="rounded border border-slate-300 px-2 py-0.5 text-xs"
          aria-label="Disposition filter"
        >
          <option value="">Disposition…</option>
          {DISPOSITION_ORDER.map((d) => (
            <option key={d} value={d}>{dispositionFilter.includes(d) ? `✓ ${DISPOSITION_LABELS[d]}` : DISPOSITION_LABELS[d]}</option>
          ))}
          <option value={NO_DISPOSITION}>{dispositionFilter.includes(NO_DISPOSITION) ? '✓ No disposition' : 'No disposition'}</option>
        </select>
      )}

      {/* Date range */}
      <div className="flex gap-2 w-full sm:w-auto items-center">
        <span className="text-slate-500 sm:hidden">Date Range</span>
        <input
          type="date"
          value={dateRange.from}
          onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
          className="flex-1 sm:flex-none rounded border border-slate-300 px-2 py-0.5"
          title="From date"
        />
        <input
          type="date"
          value={dateRange.to}
          onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
          className="flex-1 sm:flex-none rounded border border-slate-300 px-2 py-0.5"
          title="To date"
        />
      </div>

      {/* Active tag badges */}
      {tagFilter.map((t) => (
        <span key={t} className="flex items-center gap-1 rounded bg-primary-100 px-2 py-0.5 text-primary-700">
          {t}
          <button onClick={() => toggleItem(tagFilter, t, setTagFilter)} className="hover:text-primary-900">&times;</button>
        </span>
      ))}

      {/* Active disposition badges */}
      {isApplicationBoard && dispositionFilter.map((d) => (
        <span key={d} className="flex items-center gap-1 rounded bg-primary-100 px-2 py-0.5 text-primary-700">
          {d === NO_DISPOSITION ? 'No disposition' : DISPOSITION_LABELS[d]}
          <button onClick={() => toggleItem(dispositionFilter, d, setDispositionFilter)} className="hover:text-primary-900">&times;</button>
        </span>
      ))}

      {hasFilters && (
        <button onClick={clearAll} className="text-slate-500 hover:text-slate-700 underline">Clear</button>
      )}
    </div>
  );
}
